import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AuthenticationService } from './security/authentication.service';
import { CanActivateChildAuthGuard } from './security/canActivateChildAuthGuard';
import { DashboardService } from './services/dashboard.service';
import { SuiteService } from './services/suite.service';
import { EventEmitterService } from './services/event-emitter.service';
import { ApiService } from './services/api.service';
import { RoleManagementService } from './security/role-management.service';
import { CanActivateChildRoleGuard } from './security/canActivateChildRoleGuard';
import { AlertService } from './services/alert.service';
import { GlobalErrorHandler } from './services/globalErrorHandler.service';
import { ServerErrorInterceptor } from './services/serverErrorInterceptor.service';
import { CommonService } from './services/common.service';
import { ReportPackService } from './services/reportPack.service';
import { DataIngestionService } from './services/dataIngestion.service';
import { UserManagementService } from './services/user.service';
import { AnalyticService } from './services/analytics.service';
import { ToastrModule } from 'ngx-toastr';
import { PermissionManagementService } from './services/permissionManagement.service';
import { RolePermissionManagementService } from './services/rolePermissionManagement.service';
import { ReportScheduleService } from './services/reportSchedule.service';
import { PrivacyNoticeService } from './services/privacyNotice.service';
import { BundleTypeManagementService } from './services/bundleTypeManagementService';
import { ReportBundlingService } from './services/reportBundling.service';
import { ReportDownloaderService } from './services/reportDownloader.service';
import { MsalModule, MsalService, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalInterceptor, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalGuard, MsalBroadcastService } from "@azure/msal-angular";
import { PublicClientApplication, InteractionType, IPublicClientApplication, BrowserCacheLocation, LogLevel } from "@azure/msal-browser";
import { msalConfig } from './security/msal.config';
import { ReportTreeService } from './services/reportTree.service';
import { QueryEngineService } from './services/query-engine.service';
import { ReportPackSequenceService } from './services/reportPackSequence.service';
import { ApplicationConfigurationService } from './services/applicationConfigurationService';
import { RefDataService } from './services/ref-data.service';
import { environment } from 'src/environments/environment';

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

export function loggerCallback(logLevel: LogLevel, message: string) {
  // console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
     auth: {
      clientId: environment.azure_application_id,
      authority: 'https://login.microsoftonline.com/'+ environment.azure_tenant_id,
      redirectUri: window.location.origin + '/',
      postLogoutRedirectUri: window.location.origin + '/Logout',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    },
     system: {
       loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Verbose,
        piiLoggingEnabled: false,
      },
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

// MSAL Guard is required to protect routes and require authentication before accessing protected routes
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return { 
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read']
    }
  };
}

// MSAL must be initialized before any other MSAL API is called (msal-browser v3+ requirement)
export function initializeMsal(msalService: MsalService): () => Promise<void> {
  return () => msalService.instance.initialize();
}

// const protectedResourceMap = new Map<string, Array<string>>([
//   ["https://graph.microsoft.com/v1.0/me", ["user.read"]]
// ]);

// const msalInstance = new PublicClientApplication(msalConfig());

// export const msalGuardConfig: MsalGuardConfiguration = {
//   interactionType: InteractionType.Redirect, // or InteractionType.Popup
//   authRequest: {
//     scopes: ['user.read']
//   }
// };

// const msalInterceptorConfig: MsalInterceptorConfiguration = {
//   interactionType: InteractionType.Redirect,
//   protectedResourceMap
// };

@NgModule({
    declarations: [
    ],
    imports: [
        MsalModule,
        ToastrModule,
        ],
    exports: [
    ],
    providers: [
          {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMsal,
      deps: [MsalService],
      multi: true
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    
        CanActivateChildAuthGuard,
        CanActivateChildRoleGuard,
        AuthenticationService,
        DashboardService,
        SuiteService,
        EventEmitterService,
        ApiService,
        RoleManagementService,
        AlertService,
        CommonService,
        ReportPackService,
        DataIngestionService,
        UserManagementService,
        AnalyticService,
        PermissionManagementService,
        RolePermissionManagementService,
        ReportScheduleService,
        BundleTypeManagementService,
        ReportBundlingService,
        PrivacyNoticeService,
        ReportDownloaderService,
        ReportTreeService,
        QueryEngineService,
        ReportPackSequenceService,
        ApplicationConfigurationService,
        RefDataService,
        {
            provide: ErrorHandler, useClass: GlobalErrorHandler
        },
        {
            provide: HTTP_INTERCEPTORS, useClass: ServerErrorInterceptor, multi: true
        },
    ]

})
export class CoreModule { }
